/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { utils, Graphics, Sprite, Container, DisplayObject, IRenderer, Point } from 'pixi.js-legacy';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, first, takeUntil } from 'rxjs/operators';

import { TICKET_TEXTURE_OPTIONS } from '../tickets/TICKET_TEXTURE_OPTIONS';
import { SpritePool, TextPool } from 'src/app/app-data/models/data-types/GenericPool';
import { Line } from 'src/app/app-data/models/Line';

import { RenderDependencies } from '@appPlanApplication/RenderDependencies';
import { Camera } from '@appCanvasCommon/core/Camera';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { convertHexStringToNumber } from 'utils/color-utils';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { FancyText } from '@appCanvasCommon/fontpath/FancyText';
import { planRendererInfo } from '@appCanvasCommon/core/RendererInfo';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { LevelOfDetail, LEVEL_OF_DETAIL_SIGNALS, LEVEL_OF_DETAIL_TABLE } from '@appPlanApplication/lod';
import { cssVar } from '@touchplan/css-var';
import RBush from 'rbush';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';

const DISPLAY_TWO_TEXTS_DISTANCE = 2000;
const TEXT_SPACING = 20;

interface ITreeItem {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
}

export class DependencyLines {
	private makeBoom = [];
	private onViewportChanged;
	private shutdownSubject$: Subject<void> = new Subject<void>();
	private textContainer: Container;
	private enableDependencyText = false;
	private rbush: RBush;
	private enableDependencyText$ = this.userPreferenceStateService.enableDependencyText$;

	constructor(
		private canvasAngularLand: CanvasAngularLand,
		private cameraContainer: Container,
		private backgroundContainer: FancyContainer,
		private camera: Camera,
		private renderer: IRenderer,
		private safeRender: () => void,
		private userPreferenceStateService: UserPreferenceStateService
	) {
		const graphics = new FancyGraphics();
		// graphics.lineAlignment = 0.5;
		const timelineGraphics = new FancyGraphics();
		const arrowHeadContainer = new Container();
		this.textContainer = new Container();
		const graphicsBackground = new FancyGraphics();
		const arrowHeadMaker = () => {
			const spirit = new Sprite(utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_DEPENDENCY_ARROWHEAD]);
			spirit.anchor.set(0.5, 0.15);
			spirit.scale.set(0.5);
			return spirit;
		};

		const textMaker = () => {
			let text: FancyText | HighResolutionText;
			if (planRendererInfo.isWebGL()) {
				text = new FancyText('Roboto');
				text.fontSize = 32; // 32px * 3x scale factor = 96;
			} else {
				text = new HighResolutionText('', new FancyTextStyle({ fontFamily: 'Roboto', fontSize: 32 }));
			}
			text.active = true;
			text.visible = true;
			text.enableTrimText = false;
			return text;
		};
		this.rbush = new RBush();

		const arrowheadPool1 = new SpritePool(arrowHeadMaker, arrowHeadContainer);
		const arrowheadPool2 = new SpritePool(arrowHeadMaker, arrowHeadContainer);
		const arrowheadPool3 = new SpritePool(arrowHeadMaker, arrowHeadContainer);
		const textPool = new TextPool(textMaker, this.textContainer);
		const textPoolTimeline = new TextPool(textMaker, this.textContainer);

		cameraContainer.addChild(graphics, timelineGraphics, arrowHeadContainer, this.textContainer);
		backgroundContainer.addChild(graphicsBackground);

		//now cache and listen for camera events
		let lastTimelineLines = [];
		const greyLineColor = convertHexStringToNumber('#cccccc');
		//wait for the first tick of loaded
		combineLatest([RenderDependencies.loaded$.pipe(first()), this.canvasAngularLand.dependencies.line$])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(([loaded, lines]) => {
				const simpleLines = [];
				const simpleGreyLines = [];
				lastTimelineLines = [];

				lines.forEach((line) => {
					if (line.start.yIsScreen || line.end.yIsScreen) {
						lastTimelineLines.push(line);
					} else if (line.color === greyLineColor) {
						simpleGreyLines.push(line);
					} else {
						simpleLines.push(line);
					}
				});

				this.rbush.clear();
				textPool.reset();
				textPoolTimeline.reset();
				this.renderDependencyLines(simpleGreyLines, graphicsBackground, arrowheadPool1, textPool);
				this.renderDependencyLines(simpleLines, graphics, arrowheadPool2, textPool);
				this.renderDependencyLines(lastTimelineLines, timelineGraphics, arrowheadPool3, textPoolTimeline);
				// this.render();
				this.rbush.load(this.textContainer.children);
				this.safeRender();
			});

		this.onViewportChanged = () => {
			textPoolTimeline.reset();
			this.renderDependencyLines(lastTimelineLines, timelineGraphics, arrowheadPool3, textPoolTimeline);
		};

		camera.on('viewportChanged', this.onViewportChanged);

		renderer.on('prerender', this.onPreRender);

		// Dependency text
		this.updateTextVisibility();
		LevelOfDetail.emitter.on(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.updateTextVisibility);

		// Handle showing/hiding of dependency texts
		this.enableDependencyText$.pipe(takeUntil(this.shutdownSubject$)).subscribe((enableDependencyText) => {
			this.enableDependencyText = enableDependencyText;
			this.updateTextVisibility();
		});

		// Handle transparency of dependency lines
		this.canvasAngularLand.filterTransparencyValue$
			.pipe(distinctUntilChanged(), takeUntil(this.shutdownSubject$))
			.subscribe((filterTransparencyValue) => {
				if (typeof filterTransparencyValue === 'number') {
					this.backgroundContainer.alpha = filterTransparencyValue;
					this.cameraContainer.alpha = filterTransparencyValue;
				} else {
					this.backgroundContainer.alpha = 1;
					this.cameraContainer.alpha = 1;
				}
			});

		this.makeBoom.push(
			graphics,
			timelineGraphics,
			arrowHeadContainer,
			arrowheadPool1,
			arrowheadPool2,
			arrowheadPool3,
			graphicsBackground,
			textPool,
			textPoolTimeline
		);
	}

	// Cull text visibility
	private onPreRender = () => {
		// No need to process if the container is invisible
		if (!this.textContainer.visible) {
			return;
		}
		// Turn off renderables
		const allItems: ITreeItem[] = this.rbush.all();
		for (const item of allItems) {
			(<DisplayObject>(<any>item)).renderable = false;
		}
		// Turn on renderable items
		const visibleItems: ITreeItem[] = this.rbush.search({
			minX: this.camera.globalView.left,
			minY: this.camera.globalView.top,
			maxX: this.camera.globalView.right,
			maxY: this.camera.globalView.bottom,
		});
		for (const item of visibleItems) {
			(<DisplayObject>(<any>item)).renderable = true;
		}
		// console.log('visible: ' + visibleItems.length + ', hidden:' + (allItems.length - visibleItems.length));
	};

	private updateTextVisibility = () => {
		this.textContainer.visible =
			this.enableDependencyText && LevelOfDetail.getLODLevel() > LEVEL_OF_DETAIL_TABLE.VERY_LOW;
	};

	private renderDependencyLines(lines: Line[], graphics: Graphics, pool: SpritePool, textPool: TextPool) {
		// let timelineLines = [];

		graphics.clear();
		pool.reset();

		const drawText = this.canvasAngularLand.featureFlagManager.getFlag(
			DynamicFlagTypes.TICKET_DEPENDENCY_LINKAGE_TYPE_AND_LAG
		);

		lines.forEach((line) => {
			// const hasShifted = line.start.yIsScreen || line.end.yIsScreen;
			const shiftedStart = line.start.yIsScreen
				? new Point(line.start.x, this.camera.screenToRealY(line.start.y))
				: line.start;
			const shiftedEnd = line.end.yIsScreen
				? new Point(line.end.x, this.camera.screenToRealY(line.end.y))
				: line.end;

			graphics.lineStyle(10, line.color, 1.0, 0.5);
			graphics.moveTo(shiftedStart.x, shiftedStart.y);
			graphics.lineTo(shiftedEnd.x, shiftedEnd.y);

			const arrowAngle = this.getArrowHeadAngle(shiftedStart, shiftedEnd);
			const arrowSprite = pool.grab();
			arrowSprite.x = shiftedEnd.x;
			arrowSprite.y = shiftedEnd.y;
			arrowSprite.tint = line.color;
			arrowSprite.rotation = arrowAngle;

			if (drawText && line.text) {
				const d = new Point(shiftedEnd.x - shiftedStart.x, shiftedEnd.y - shiftedStart.y);
				const magnitude = Math.sqrt(d.x * d.x + d.y * d.y);
				if (magnitude > DISPLAY_TWO_TEXTS_DISTANCE) {
					this.createText(line.text, textPool, shiftedEnd.x - TEXT_SPACING, shiftedEnd.y, -1);
					this.createText(line.text, textPool, shiftedStart.x + TEXT_SPACING, shiftedStart.y, 0);
				} else {
					this.createText(
						line.text,
						textPool,
						(shiftedStart.x + shiftedEnd.x) / 2,
						(shiftedStart.y + shiftedEnd.y) / 2,
						-0.5
					);
				}
			}
		});
		// this.render();

		// return timelineLines;
	}

	/**
	 * Creates a text object with the label specified and adds it to the quadtree
	 * @param label text label to add
	 * @param textPool textpool to get the text object from
	 * @param x x coordinte
	 * @param y y coordinate
	 * @param widthOffset percentag of resulting width to add to the x position
	 */
	private createText(label: string, textPool: TextPool, x: number, y: number, widthOffset: number) {
		const text = textPool.grab();
		text.text = label;
		//reset everything pulled out of the pool to renderable, assuming the rBush will re-set it as appropriate
		text.renderable = true;
		text.calculateText();
		this.setTextColor(text, cssVar.black_number);
		const width = this.getTextWidth(text);
		text.x = x + widthOffset * width;
		text.y = y;

		// Prepare for rbush data structure
		const t = <ITreeItem>(<any>text);
		t.minX = text.x;
		t.minY = text.y;
		t.maxX = text.x + width;
		t.maxY = text.y + text.height;
	}

	private getTextWidth(text: FancyText | HighResolutionText) {
		if (text.isFancy) {
			return (text as FancyText).widthPx;
		}
		return text.width;
	}

	private setTextColor(text: FancyText | HighResolutionText, color: number) {
		if (!text.isFancy) {
			(text as HighResolutionText).style.fill = color;
		} else {
			text.tint = color;
		}
	}

	private getArrowHeadAngle(startPoint: Point, endPoint: Point) {
		const dx = endPoint.x - startPoint.x;
		const dy = endPoint.y - startPoint.y;
		const norm = Math.sqrt(dx * dx + dy * dy);

		const ndx = dx / norm;
		const ndy = dy / norm;

		const baseAngle = Math.atan2(ndx, ndy);
		return Math.PI - baseAngle;
	}

	public destroy() {
		this.shutdownSubject$.next();
		window.perf.log('start destroy dependencies');
		this.makeBoom.forEach((thing) => (thing.destroy ? thing.destroy() : null));
		this.makeBoom = null;
		this.camera.off('viewportChanged', this.onViewportChanged);
		this.camera = null;
		this.renderer.off('prerender', this.onPreRender);
		this.renderer = null;
		LevelOfDetail.emitter.off(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.updateTextVisibility);
		this.textContainer = null;
		window.perf.log('end destroy dependencies', { previousEvent: 'start destroy dependencies' });
	}
}
